import { CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PriorityIcon } from '@/components/common/priority-icon';
import { Badge } from '@/components/ui/badge';
import { RECIPIENT_BADGE_VARIANT, type RecipientBadgesVariant } from '@/constants';
import {
  collapseRolesToAll,
  getRoleDisplayName,
  mapRoleIdentifiersToShortNames,
} from '@/lib/helpers/role-mapper';
import { getRoleBadgeClassName, getRoleTagClassName } from '@/lib/helpers/role-variants';
import { cn } from '@/lib/utils';
import { ALL_RECIPIENT_ROLE_KEYS, ROLES } from '@/utils/constants/roles';

interface RecipientBadgesProps {
  recipientIds: string[];
  variant?: RecipientBadgesVariant;
  prioritized?: boolean;
  collapseToAll?: boolean;
  confirmedRoles?: string[];
}

const PRIORITY_ICON_CLASS_NAME: Record<RecipientBadgesVariant, string> = {
  [RECIPIENT_BADGE_VARIANT.PILL]:
    'absolute -top-1.5 -right-1.5 size-4 [--priority-icon-border:var(--white)]',
  [RECIPIENT_BADGE_VARIANT.TAG]: 'size-4 dark:[--priority-icon-border:var(--white)]',
};

function getPrioritizedShortName(shortNames: string[]): string | undefined {
  return [ROLES.lf.shortName, ROLES.all.shortName].find((shortName) =>
    shortNames.includes(shortName)
  );
}

export function RecipientBadges({
  recipientIds,
  variant = RECIPIENT_BADGE_VARIANT.PILL,
  prioritized = false,
  collapseToAll = true,
  confirmedRoles = [],
}: RecipientBadgesProps) {
  const { t } = useTranslation();

  if (!recipientIds?.length) {
    return null;
  }

  const roleIds = collapseToAll
    ? collapseRolesToAll(recipientIds, ALL_RECIPIENT_ROLE_KEYS)
    : recipientIds;

  const shortNames = mapRoleIdentifiersToShortNames(roleIds).sort((a, b) => a.localeCompare(b));

  const prioritizedShortName = prioritized ? getPrioritizedShortName(shortNames) : undefined;
  const confirmedShortNames = new Set(mapRoleIdentifiersToShortNames(confirmedRoles));

  return shortNames.map((shortName) => {
    const priorityIcon = shortName === prioritizedShortName && (
      <PriorityIcon
        className={cn(
          PRIORITY_ICON_CLASS_NAME[variant],
          shortName === ROLES.lf.shortName && 'text-chat-message-badgelf-fill'
        )}
      />
    );

    if (variant === RECIPIENT_BADGE_VARIANT.TAG) {
      return (
        <Badge
          key={shortName}
          className={cn(
            'pointer-events-none h-5.5 justify-center gap-1 rounded-md border px-2 text-xs font-medium shadow-none',
            getRoleTagClassName(shortName)
          )}
        >
          {confirmedShortNames.has(shortName) && (
            <CheckCheck
              strokeWidth={3}
              aria-hidden
              data-testid="confirmed-icon"
              className="size-3"
            />
          )}
          {getRoleDisplayName(shortName, t)}
          {priorityIcon}
        </Badge>
      );
    }

    return (
      <span key={shortName} className="relative m-0.5 inline-flex">
        <Badge
          className={cn(
            'pointer-events-none h-6 justify-center rounded-full p-1 px-2 text-[10px] font-semibold',
            getRoleBadgeClassName(shortName)
          )}
        >
          {getRoleDisplayName(shortName, t)}
        </Badge>
        {priorityIcon}
      </span>
    );
  });
}
